import React from "react";
import { useCasinoPage } from "../CasinoPageContext";
import { HTMLService } from "../services/html";
import { FaShieldAlt, FaGift, FaFootballBall } from "react-icons/fa";

const HeroContent = () => {
  const { website } = useCasinoPage();

  const h1Title =
    website?.homepageContent?.welcomeSection.title ||
    website?.homepageContent?.h1 ||
    "";

  const contentHtml = website?.homepageContent?.welcomeSection.content || "";
  const paragraphs = contentHtml
    .split("</p>")
    .map((p) => p.replace("<p>", "").trim())
    .filter(Boolean);

  // Remove exact duplicate paragraphs to reduce on-page text duplication
  const uniqueParagraphs = Array.from(new Set(paragraphs));

  const isOdd = uniqueParagraphs.length % 2 !== 0;
  const middleIndex = Math.floor(uniqueParagraphs.length / 2);

  const leftColumn = isOdd
    ? uniqueParagraphs.slice(0, middleIndex)
    : uniqueParagraphs.slice(0, middleIndex);
  const rightColumn = isOdd
    ? uniqueParagraphs.slice(middleIndex + 1)
    : uniqueParagraphs.slice(middleIndex);
  const centerParagraph = isOdd ? uniqueParagraphs[middleIndex] : null;

  const orderedParagraphs = [
    ...leftColumn,
    ...(centerParagraph ? [centerParagraph] : []),
    ...rightColumn,
  ];

  return (
    <section
      id="hero-content"
      className="relative bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white py-20 md:py-28 overflow-hidden flex justify-center"
    >
      <div className="absolute inset-0 bg-black/25" />

      <div className="relative z-10 max-w-7xl w-full px-6 sm:px-8 lg:px-12">
        {/* Casino24 quick-access tabs */}
        <div className="mb-8 flex justify-center px-2">
          <nav
            className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-1 w-full sm:w-auto max-w-md rounded-2xl sm:rounded-full bg-white/5 border border-cyan-400/25 px-2 py-2 sm:px-1 sm:py-1 backdrop-blur-sm"
            aria-label="Casino24 quick links"
          >
            <a
              href="https://www.casino24.com/casinos"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 px-4 py-2 sm:py-1.5 rounded-xl sm:rounded-full text-xs sm:text-[11px] font-medium text-gray-100 bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-400/30 hover:border-cyan-400/60 transition-colors w-full sm:w-auto justify-start sm:justify-center"
            >
              <FaShieldAlt className="h-3.5 w-3.5 text-cyan-300" />
              <span>Verified Casinos</span>
            </a>
            <a
              href="https://www.casino24.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 px-4 py-2 sm:py-1.5 rounded-xl sm:rounded-full text-xs sm:text-[11px] font-medium text-gray-200 hover:text-white bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-300/30 hover:border-yellow-300/60 transition-colors w-full sm:w-auto justify-start sm:justify-center"
            >
              <FaGift className="h-3.5 w-3.5 text-yellow-300" />
              <span>Exclusive Deals</span>
            </a>
            <a
              href="https://www.casino24.com/sports-betting"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 px-4 py-2 sm:py-1.5 rounded-xl sm:rounded-full text-xs sm:text-[11px] font-medium text-gray-200 hover:text-white bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-300/30 hover:border-emerald-300/60 transition-colors w-full sm:w-auto justify-start sm:justify-center"
            >
              <FaFootballBall className="h-3.5 w-3.5 text-emerald-300" />
              <span>Sports Betting</span>
            </a>
          </nav>
        </div>

        {/* Section intro */}
        <div className="mb-8 md:mb-10 text-center md:text-left max-w-7xl mx-auto flex flex-col items-center">
          <p
            className="inline-flex items-center justify-center px-3 py-1 mb-3 text-[11px] md:text-xs font-semibold 
          uppercase tracking-[0.25em] text-cyan-300/80 bg-white/5 border border-cyan-400/30 rounded-full backdrop-blur-sm"
          >
            CasinoB8 overview
          </p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight text-white">
            A clear, calm introduction to your casino journey
          </h2>
          <p className="mt-4 text-gray-300 text-sm md:text-base leading-relaxed">
            Below is a concise overview of what CasinoB8 stands for, written in
            soft, easy-to-read paragraphs so you can get the full picture at a
            glance.
          </p>
          {h1Title && (
            <p className="mt-2 text-gray-300 text-xs md:text-sm leading-relaxed max-w-3xl mx-auto">
              This section expands on <span className="font-semibold">{h1Title}</span> so
              that the main heading and page content stay perfectly aligned for
              both readers and search engines.
            </p>
          )}
        </div>

        {/* Paragraph flow */}
        <div className="max-w-7xl mx-auto space-y-4 md:space-y-5 text-left">
          {orderedParagraphs.map((p, idx) => (
            <p
              key={idx}
              className={`text-gray-200 leading-relaxed md:leading-relaxed ${
                idx === 0
                  ? "text-base md:text-lg text-gray-100 "
                  : "text-sm md:text-base"
              }`}
            >
              {HTMLService.toReact(p)}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroContent;
